import { useTranslation } from 'react-i18next'
import { Panel } from '@/components/forge/Panel'
import { PlainBadge } from '@/components/forge/StatusBadge'
import { Eyebrow, Num } from '@/components/forge/primitives'
import { toAnalysisRun } from '@/features/anomalies/api'
import { formatDateTime } from '@/lib/format'
import { IST_STAMP } from './time'

/**
 * Analysis · Reasoning — how the engine reached its conclusion.
 *
 * ── `content.run`, not `history` ─────────────────────────────────────────
 * Since 2026-10-12 the engine writes its own account of each run: five steps in
 * a fixed order — detected, looked for context, listed possible causes, weighed
 * them, concluded — each a heading and a few finished sentences, plus when the
 * run started and finished and a structured verdict. This view draws exactly
 * that. Every sentence arrives finished, so nothing here composes wording; the
 * numbering is the order the steps arrive in.
 *
 * ── `history` stays, under it, as "Changes" ─────────────────────────────
 * `Anomaly.history` is the backend's log of writes to `content`/`title`, one
 * entry per analysis. It is no longer how the reasoning is told, but it is
 * still the only record of WHEN the analysis was rewritten, so it is kept as a
 * short list beneath the run. (Removed 2026-10-13, restored 2026-10-14.)
 *
 * ── Older records fall back to history alone ────────────────────────────
 * Alerts analysed before the change have no `run`, or one in an older shape
 * (`model`/`consulted`/`candidates`). `toAnalysisRun` answers null for both, and
 * the view is what it was: the change log, until somebody re-runs the analysis.
 */
export function ReasoningView({ anomaly }) {
  const { t } = useTranslation(['app', 'common'])
  const run = toAnalysisRun(anomaly?.content)
  const history = anomaly?.history ?? []

  if (!run && !history.length) {
    return (
      <Panel className="flex flex-col gap-1 py-10 text-center">
        <p className="m-0 text-body font-semibold text-ink">{t('anomalies.analysis.reasoning.empty')}</p>
        <p className="m-0 text-body-sm text-ink-dim">{t('anomalies.analysis.reasoning.emptyBody')}</p>
      </Panel>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {run ? <RunPanel run={run} /> : null}
      {history.length ? <HistoryPanel history={history} titled={Boolean(run)} /> : null}
    </div>
  )
}

function RunPanel({ run }) {
  const { t } = useTranslation(['app', 'common'])
  const { verdict } = run

  return (
    <Panel className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <Eyebrow as="h2" className="me-auto">
          {t('anomalies.analysis.reasoning.title')}
        </Eyebrow>
        {/* The verdict, as the engine structured it. Plain badges: these are
            facts about the conclusion, not states that need a colour to warn. */}
        {verdict?.faultDomain ? (
          <PlainBadge>
            {t(`anomalies.analysis.reasoning.domain.${verdict.faultDomain}`, {
              defaultValue: t('anomalies.analysis.reasoning.domainOther', { domain: verdict.faultDomain }),
            })}
          </PlainBadge>
        ) : null}
        {verdict?.likelihood ? (
          <PlainBadge>
            {t(`anomalies.analysis.reasoning.likelihood.${verdict.likelihood}`, {
              defaultValue: t('anomalies.analysis.reasoning.likelihoodOther', { likelihood: verdict.likelihood }),
            })}
          </PlainBadge>
        ) : null}
        {verdict?.tie ? <PlainBadge>{t('anomalies.analysis.reasoning.tie')}</PlainBadge> : null}
      </div>

      <ol className="m-0 flex list-none flex-col gap-4 p-0">
        {run.steps.map((entry, index) => (
          <li key={`${entry.step ?? 'step'}-${index}`} data-step={entry.step ?? undefined} className="flex gap-3">
            <span
              aria-hidden="true"
              className="num flex size-6 shrink-0 items-center justify-center rounded-full border border-hairline bg-sunken text-micro font-semibold text-ink"
            >
              {index + 1}
            </span>
            <div className="min-w-0 flex-1">
              <h3 className="m-0 text-body-sm font-semibold text-ink">
                <span className="sr-only">{t('anomalies.analysis.reasoning.stepNumber', { n: index + 1 })} </span>
                {entry.title}
              </h3>
              {/* One sentence per line, as the engine wrote them. */}
              {entry.found.map((line, lineIndex) => (
                <p key={lineIndex} className="m-0 mt-1 text-body-sm text-ink-dim">
                  {line}
                </p>
              ))}
            </div>
          </li>
        ))}
      </ol>

      {run.finishedAt || run.seconds ? (
        <p className="m-0 border-t border-hairline pt-3 text-micro font-normal text-ink-mute">
          <Num>
            {[
              run.finishedAt
                ? t('anomalies.analysis.reasoning.analysedAt', { when: formatDateTime(run.finishedAt, IST_STAMP) })
                : null,
              run.seconds ? t('anomalies.analysis.reasoning.took', { seconds: run.seconds }) : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Num>
        </p>
      ) : null}
    </Panel>
  )
}

/**
 * The backend's log of writes to the analysis, newest first. The whole story
 * on a record with no run; a short "Changes" list under one that has.
 *
 * `_append_history` carries no actor, so an entry says what changed and when
 * and never who. Recorded in BACKEND-GAPS.md.
 */
function HistoryPanel({ history, titled }) {
  const { t } = useTranslation(['app', 'common'])

  return (
    <Panel className="flex flex-col gap-3">
      <Eyebrow as="h2">{titled ? t('anomalies.analysis.reasoning.changes') : t('anomalies.detail.history')}</Eyebrow>
      <ol className="relative m-0 flex list-none flex-col gap-3.5 p-0 ps-4">
        {/* Logical properties only — the rule runs down the other edge under RTL. */}
        <span aria-hidden="true" className="absolute start-[3px] top-1.5 bottom-1.5 w-px bg-hairline" />
        {[...history].reverse().map((entry, index) => (
          <li key={`${entry.changedAt}-${index}`} className="relative">
            <span
              aria-hidden="true"
              className="absolute top-1.5 size-[9px] rounded-full bg-violet ring-2 ring-card"
              style={{ insetInlineStart: '-13px' }}
            />
            <p className="m-0 text-body-sm font-semibold text-ink">{entry.title}</p>
            {entry.description ? <p className="m-0 mt-0.5 text-body-sm text-ink-dim">{entry.description}</p> : null}
            <Num className="mt-0.5 block text-micro font-normal text-ink-mute">
              {entry.changedAt ? formatDateTime(entry.changedAt, IST_STAMP) : '—'}
            </Num>
          </li>
        ))}
      </ol>
    </Panel>
  )
}
